import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { Button, Table, Card, Alert } from "react-bootstrap";
import { useParams, useHistory } from "react-router-dom";
import { useStores } from "../../context/mobx-context";

function DeeplinkAnalytics() {
  const { path } = useParams();
  const { deeplinksStore } = useStores();
  const history = useHistory();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const decodedPath = decodeURIComponent(path);

  useEffect(() => {
    if (decodedPath) {
      deeplinksStore.fetchPathStats(decodedPath).finally(() => {
        setIsInitialLoad(false);
      });
    }
  }, [deeplinksStore, decodedPath]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = deeplinksStore.getPathStats(decodedPath);

  if (isInitialLoad || (deeplinksStore.isFetching && !stats)) {
    return (
      <div className="container-fluid px-4 py-3">
        <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
          <div>
            <h2 className="mb-1">Analytics Overview</h2>
            <p className="text-muted mb-0">
              <code className="bg-white px-2 py-1 rounded">{decodedPath}</code>
            </p>
          </div>
          <Button variant="outline-secondary" onClick={() => history.goBack()}>
            ← Back
          </Button>
        </div>
        <Card className="text-center shadow-sm">
          <Card.Body className="p-5">
            <div className="spinner-border text-secondary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 className="text-muted mb-2">Loading Analytics Data</h5>
            <p className="text-muted mb-0">Please wait while we fetch the analytics for this deeplink...</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="container-fluid px-4 py-3">
        <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
          <div>
            <h2 className="mb-1">Analytics Overview</h2>
            <p className="text-muted mb-0">
              <code className="bg-white px-2 py-1 rounded">{decodedPath}</code>
            </p>
          </div>
          <Button variant="outline-secondary" onClick={() => history.goBack()}>
            ← Back
          </Button>
        </div>
        <Alert variant="warning" className="shadow-sm">
          <div className="d-flex align-items-start">
            <div className="me-3">
              <span className="display-4">⚠️</span>
            </div>
            <div>
              <Alert.Heading className="h4">No Analytics Data Found</Alert.Heading>
              <p className="mb-3">
                No analytics data was found for the deeplink path <code className="bg-light px-2 py-1 rounded">"{decodedPath}"</code>.
              </p>
              <p className="mb-2">This could mean:</p>
              <ul className="mb-0">
                <li>This deeplink hasn't been clicked yet</li>
                <li>The path doesn't exist in the tracking system</li>
                <li>Analytics tracking hasn't been set up for this path</li>
              </ul>
            </div>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-3">
      <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
        <div>
          <h2 className="mb-1">Analytics Overview</h2>
          <p className="text-muted mb-0">
            <code className="bg-white px-2 py-1 rounded">{decodedPath}</code>
          </p>
        </div>
        <Button variant="outline-secondary" onClick={() => history.goBack()}>
          ← Back
        </Button>
      </div>

      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-dark text-white">
          <h5 className="mb-0">📊 Overall Metrics</h5>
        </Card.Header>
        <Card.Body className="p-4">
          <div className="row g-4">
            <div className="col-lg-2 col-md-4">
              <div className="text-center p-3 border rounded bg-light">
                <h2 className="text-dark mb-2 fw-bold">{stats?.totalClicks || 0}</h2>
                <p className="text-muted mb-0 fw-semibold">Total Clicks</p>
                <small className="text-muted">All time</small>
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <div className="text-center p-3 border rounded bg-success-subtle">
                <h2 className="text-success mb-2 fw-bold">{stats?.totalAppOpens || 0}</h2>
                <p className="text-muted mb-0 fw-semibold">Total App Opens</p>
                <small className="text-muted">All time</small>
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <div className="text-center p-3 border rounded bg-light">
                <h2 className="text-secondary mb-2 fw-bold">{stats?.basePathClicks || 0}</h2>
                <p className="text-muted mb-0 fw-semibold">Direct Clicks</p>
                <small className="text-muted">No UTM parameters</small>
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <div className="text-center p-3 border rounded bg-success-subtle">
                <h2 className="text-success mb-2 fw-bold">{stats?.basePathAppOpens || 0}</h2>
                <p className="text-muted mb-0 fw-semibold">Direct App Opens</p>
                <small className="text-muted">No UTM parameters</small>
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <div className="text-center p-3 border rounded bg-light">
                <p className="mb-2 fw-semibold text-dark">Last Activity</p>
                <p className="text-muted mb-0">{formatDate(stats?.basePathLastClicked)}</p>
                <small className="text-muted">Most recent click</small>
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <div className="text-center p-3 border rounded bg-light">
                <p className="mb-2 fw-semibold text-dark">First Created</p>
                <p className="text-muted mb-0">{formatDate(stats?.basePathCreated)}</p>
                <small className="text-muted">Initial tracking</small>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-secondary text-white">
          <h5 className="mb-0">🔗 Deeplink Path</h5>
        </Card.Header>
        <Card.Body className="p-4">
          <div className="d-flex align-items-center gap-3">
            <div className="flex-grow-1">
              <code className="d-block p-3 bg-light border rounded fs-6">
                {decodedPath}
              </code>
            </div>
            <Button 
              variant="outline-secondary" 
              size="sm"
              className="px-3 py-2"
              onClick={() => navigator.clipboard.writeText(decodedPath)}
            >
              📋 Copy Path
            </Button>
          </div>
          <small className="text-muted mt-2 d-block">
            This is the deeplink path that is being tracked.
          </small>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Header className="bg-secondary text-white">
          <h5 className="mb-0">📈 UTM Source Breakdown</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {stats?.sources && stats.sources.length > 0 ? (
            <div className="table-responsive">
              <Table className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="text-start px-4 py-3">#</th>
                    <th className="text-start px-4 py-3">UTM Source</th>
                    <th className="text-start px-4 py-3">UTM Medium</th>
                    <th className="text-start px-4 py-3">UTM Campaign</th>
                    <th className="text-start px-4 py-3">Clicks</th>
                    <th className="text-start px-4 py-3">App Opens</th>
                    <th className="text-start px-4 py-3">Last Clicked</th>
                    <th className="text-start px-4 py-3">First Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.sources?.map((source, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'table-light' : ''}>
                      <td className="text-start px-4 py-3">{index + 1}</td>
                      <td className="text-start px-4 py-3">
                        {source.utmSource ? (
                          <code className="bg-light border px-2 py-1 rounded text-dark">{source.utmSource}</code>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-start px-4 py-3">
                        {source.utmMedium ? (
                          <code className="bg-light border px-2 py-1 rounded text-dark">{source.utmMedium}</code>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-start px-4 py-3">
                        {source.utmCampaign ? (
                          <code className="bg-light border px-2 py-1 rounded text-dark">{source.utmCampaign}</code>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-start px-4 py-3">
                        <span className="badge bg-secondary fs-6 px-3 py-2">{source.clickCount}</span>
                      </td>
                      <td className="text-start px-4 py-3">
                        <span className="badge bg-success fs-6 px-3 py-2">{source.appOpenCount || 0}</span>
                      </td>
                      <td className="text-start px-4 py-3">
                        <small className="text-muted">{formatDate(source.lastClickedAt)}</small>
                      </td>
                      <td className="text-start px-4 py-3">
                        <small className="text-muted">{formatDate(source.createdAt)}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center p-5">
              <div className="mb-3">
                <span className="display-1">📊</span>
              </div>
              <h5 className="text-muted mb-3">No UTM Tracking Data</h5>
              <p className="text-muted mb-2">No UTM tracked clicks found for this deeplink.</p>
              <small className="text-muted">
                UTM parameters help track the source and medium of clicks (e.g., from social media, email campaigns, etc.)
              </small>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default observer(DeeplinkAnalytics);